/**
 * The sheet's vertical layout arithmetic (`SheetElement.Layout`): where the
 * body, its fixed children and the legend sit below the header. Moved out of
 * sheet-element.ts (r5 FS 2); `SheetElement.Layout` is this namespace.
 */
import { SheetViewModel } from "@tento-lona";
import { SheetCellTargets } from "./sheet-cell-targets.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";

/** `SheetElement.Layout`: the header, body and legend offsets of a bound sheet. */
export namespace SheetElementLayout {
  /**
   * The 1px bottom divider the header DOM renders — reserved only when a
   * header actually exists. `headerHeight()` below already conditions on
   * this; the body/legend offsets MUST condition the same way, because a
   * headerless sheet (a custom `SystemRow.Factory` that builds no header
   * row) has no divider, and reserving the phantom pixel pushed every body
   * fixed-child — the chart canvas among them — exactly 1px below the
   * legend's y-axis ticks.
   */
  function headerDividerPx(sheet: SheetViewModel.Surface<SheetBinder>): number {
    return sheet.layout.headerHeightPx > 0 ? 1 : 0;
  }

  export function bodyMarginTop(sheet: SheetViewModel.Surface<SheetBinder>): number {
    return (
      SheetCellTargets.topContentPaddingPx +
      sheet.layout.headerHeightPx +
      headerDividerPx(sheet)
    );
  }

  /**
   * Y (scroll-relative) for a BODY fixed-child / overlay anchored at row
   * offset `offsetPx`. Fixed children are positioned against the scroll
   * container, so they must clear the header by exactly what the normal-flow
   * body column reserves through `--body-margin-top` (= `bodyMarginTop`):
   * `offsetPx` already counts the header's content height (`headerHeightPx`),
   * and `bodyMarginTop` adds the top content padding PLUS the 1px the header
   * DOM renders for its bottom divider. Hand-rolling `offsetPx +
   * topContentPadding` drops that pixel, floating every body overlay (event
   * cells, the chart canvas) 1px above its own row background and the y-axis
   * ticks. Footer offsets are pre-rebased, so non-body regions use the raw
   * offset.
   */
  export function bodyFixedChildTop(
    offsetPx: number,
    location: string,
    sheet: SheetViewModel.Surface<SheetBinder>,
  ): number {
    return location === "body"
      ? offsetPx - sheet.layout.headerHeightPx + bodyMarginTop(sheet)
      : offsetPx;
  }

  export function headerHeight(sheet: SheetViewModel.Surface<SheetBinder>): number {
    // 1) If there's visible sticky-header content, add 2px of padding
    return sheet.layout.headerHeightPx > 0
      ? sheet.layout.headerHeightPx + 1
      : sheet.layout.headerHeightPx;
  }

  /** The legend's sticky top: `0px` for the frozen pane, below the header for the scrolling one. */
  export function legendStickyTop(
    pane: "frozen" | "scrolling",
    sheet: SheetViewModel.Surface<SheetBinder>,
  ): string {
    return pane === "frozen"
      ? "0px"
      : sheet.layout.headerHeightPx +
          SheetCellTargets.topContentPaddingPx +
          headerDividerPx(sheet) +
          "px";
  }

  export function bottomOffset(): number {
    return 0;
  }
}
